import { useState, useEffect } from 'react';
import type { AdminLive, LiveStatus, LiveProvider } from '@/mocks/adminLive';
import { statusLabels, providerLabels, providerIcons, providerColors } from '@/mocks/adminLive';
import { SwalAlert } from '@/hooks/useSweetAlert';

interface LiveFormModalProps {
  open: boolean;
  live: AdminLive | null;
  providers: LiveProvider[];
  onClose: () => void;
  onSave: (data: {
    title: string;
    description: string;
    date: string;
    time: string;
    host: string;
    provider: LiveProvider;
    providerUrl: string;
    status: LiveStatus;
  }) => void;
}

export default function LiveFormModal({ open, live, providers, onClose, onSave }: LiveFormModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [host, setHost] = useState('');
  const [provider, setProvider] = useState<LiveProvider>('youtube');
  const [providerUrl, setProviderUrl] = useState('');
  const [status, setStatus] = useState<LiveStatus>('scheduled');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const isEditing = live !== null;

  useEffect(() => {
    if (open) {
      if (live) {
        setTitle(live.title);
        setDescription(live.description);
        setDate(live.date);
        setTime(live.time);
        setHost(live.host);
        setProvider(live.provider);
        setProviderUrl(live.providerUrl);
        setStatus(live.status);
      } else {
        setTitle('');
        setDescription('');
        setDate('');
        setTime('');
        setHost('');
        setProvider('youtube');
        setProviderUrl('');
        setStatus('scheduled');
      }
      setErrors({});
    }
  }, [open, live]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};

    if (!title.trim()) newErrors.title = 'El título es obligatorio';
    if (title.length > 120) newErrors.title = 'Máximo 120 caracteres';
    if (!description.trim()) newErrors.description = 'La descripción es obligatoria';
    if (description.length > 400) newErrors.description = 'Máximo 400 caracteres';
    if (!date) newErrors.date = 'La fecha es obligatoria';
    if (!time) newErrors.time = 'La hora es obligatoria';
    if (!host.trim()) newErrors.host = 'El locutor es obligatorio';
    if (provider !== 'vps' && provider !== 'mux' && !providerUrl.trim())
      newErrors.providerUrl = 'La URL es obligatoria para este proveedor';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSaving(true);
    onSave({
      title: title.trim(),
      description: description.trim(),
      date,
      time,
      host: host.trim(),
      provider,
      providerUrl: providerUrl.trim(),
      status,
    });
    onClose();
    setSaving(false);
    await SwalAlert.toast(isEditing ? 'Live actualizado.' : 'Live creado.', 'success');
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 pt-[8vh] overflow-y-auto">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-2xl w-full max-w-lg overflow-hidden shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
          <div>
            <h3 className="text-base font-bold text-foreground-950">
              {isEditing ? 'Editar live' : 'Crear live'}
            </h3>
            <p className="text-[11px] text-foreground-400 mt-0.5">
              {isEditing ? 'Modificá los datos del live' : 'Configurá una nueva transmisión en vivo'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
          >
            <i className="ri-close-line text-foreground-500"></i>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Título */}
          <div>
            <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Título del live</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
              placeholder="Ej: Tardes de Pop - En Vivo desde Cabina"
              className={`w-full px-3 py-2.5 rounded-xl bg-background-100 border text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-accent-500 transition-colors ${
                errors.title ? 'border-red-400' : 'border-background-200'
              }`}
            />
            <div className="flex items-center justify-between mt-1">
              {errors.title && <p className="text-red-500 text-[10px]">{errors.title}</p>}
              <span className="text-[9px] text-foreground-400 ml-auto">{title.length}/120</span>
            </div>
          </div>

          {/* Descripción */}
          <div>
            <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Descripción</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={400}
              rows={3}
              placeholder="Describí el contenido del live..."
              className={`w-full px-3 py-2.5 rounded-xl bg-background-100 border text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-accent-500 transition-colors resize-none ${
                errors.description ? 'border-red-400' : 'border-background-200'
              }`}
            ></textarea>
            <div className="flex items-center justify-between mt-1">
              {errors.description && <p className="text-red-500 text-[10px]">{errors.description}</p>}
              <span className="text-[9px] text-foreground-400 ml-auto">{description.length}/400</span>
            </div>
          </div>

          {/* Fecha y Hora */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Fecha</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className={`w-full px-3 py-2.5 rounded-xl bg-background-100 border text-foreground-900 text-sm focus:outline-none focus:border-accent-500 transition-colors ${
                  errors.date ? 'border-red-400' : 'border-background-200'
                }`}
              />
              {errors.date && <p className="text-red-500 text-[10px] mt-1">{errors.date}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Hora</label>
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className={`w-full px-3 py-2.5 rounded-xl bg-background-100 border text-foreground-900 text-sm focus:outline-none focus:border-accent-500 transition-colors ${
                  errors.time ? 'border-red-400' : 'border-background-200'
                }`}
              />
              {errors.time && <p className="text-red-500 text-[10px] mt-1">{errors.time}</p>}
            </div>
          </div>

          {/* Locutor */}
          <div>
            <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Locutor / Host</label>
            <input
              type="text"
              value={host}
              onChange={(e) => setHost(e.target.value)}
              placeholder="Ej: Carlos Mendoza"
              className={`w-full px-3 py-2.5 rounded-xl bg-background-100 border text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-accent-500 transition-colors ${
                errors.host ? 'border-red-400' : 'border-background-200'
              }`}
            />
            {errors.host && <p className="text-red-500 text-[10px] mt-1">{errors.host}</p>}
          </div>

          {/* Proveedor */}
          <div>
            <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Proveedor de streaming</label>
            <div className="grid grid-cols-2 gap-2">
              {providers.map((p) => {
                const pColor = providerColors[p];
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setProvider(p)}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border cursor-pointer transition-all ${
                      provider === p
                        ? `border-accent-500 bg-accent-50`
                        : 'border-background-200 bg-background-100 hover:bg-background-200'
                    }`}
                  >
                    <i className={`${providerIcons[p]} text-lg ${provider === p ? 'text-accent-600' : 'text-foreground-400'}`}></i>
                    <div className="text-left">
                      <span
                        className={`text-xs font-semibold block ${
                          provider === p ? 'text-accent-700' : 'text-foreground-600'
                        }`}
                      >
                        {providerLabels[p]}
                      </span>
                      <span className="text-[9px] text-foreground-400">
                        {p === 'vps' || p === 'mux' ? 'Próximamente' : 'Conectar'}
                      </span>
                    </div>
                    {provider === p && (
                      <i className="ri-check-line text-accent-500 text-sm ml-auto"></i>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Provider URL */}
          {(provider === 'youtube' || provider === 'facebook') && (
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
                URL de {providerLabels[provider]}
              </label>
              <input
                type="url"
                value={providerUrl}
                onChange={(e) => setProviderUrl(e.target.value)}
                placeholder={
                  provider === 'youtube'
                    ? 'https://youtube.com/live/...'
                    : 'https://facebook.com/live/...'
                }
                className={`w-full px-3 py-2.5 rounded-xl bg-background-100 border text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-accent-500 transition-colors ${
                  errors.providerUrl ? 'border-red-400' : 'border-background-200'
                }`}
              />
              {errors.providerUrl && <p className="text-red-500 text-[10px] mt-1">{errors.providerUrl}</p>}
            </div>
          )}

          {(provider === 'vps' || provider === 'mux') && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2.5">
              <i className="ri-information-line text-amber-500 text-sm mt-0.5 shrink-0"></i>
              <div>
                <p className="text-[11px] font-semibold text-amber-700">Proveedor futuro</p>
                <p className="text-[10px] text-amber-600 mt-0.5">
                  {provider === 'vps'
                    ? 'El streaming por VPS propio estará disponible próximamente. La URL se configurará cuando se active el servicio.'
                    : 'La integración con Mux estará disponible en una actualización futura.'}
                </p>
              </div>
            </div>
          )}

          {/* Estado */}
          {!isEditing && (
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Estado inicial</label>
              <div className="flex items-center gap-1">
                {(['draft', 'scheduled', 'en_vivo'] as LiveStatus[]).map((st) => (
                  <button
                    key={st}
                    type="button"
                    onClick={() => setStatus(st)}
                    className={`flex-1 px-2 py-2 rounded-xl border text-[11px] font-semibold cursor-pointer transition-all flex items-center justify-center gap-1.5 whitespace-nowrap ${
                      status === st
                        ? st === 'en_vivo'
                          ? 'border-red-500 bg-red-50 text-red-700'
                          : 'border-amber-500 bg-amber-50 text-amber-700'
                        : 'border-background-200 bg-background-100 text-foreground-400 hover:bg-background-200'
                    }`}
                  >
                    {st === 'en_vivo' && (
                      <span className={`w-1.5 h-1.5 rounded-full ${status === st ? 'bg-red-500 animate-pulse' : 'bg-foreground-300'}`}></span>
                    )}
                    {statusLabels[st]}
                  </button>
                ))}
              </div>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="flex items-center gap-2.5 px-5 py-4 border-t border-background-200 bg-background-50">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-foreground-600 bg-background-100 hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
          >
            Cancelar
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={saving}
            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-background-50 bg-accent-500 hover:bg-accent-600 cursor-pointer transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? (
              <span className="flex items-center justify-center gap-1.5">
                <i className="ri-loader-4-line animate-spin"></i>
                Guardando...
              </span>
            ) : (
              isEditing ? 'Guardar cambios' : 'Crear live'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}